import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Info, Search, Wallet } from 'lucide-react';

import { request } from '@/api/client';
import type {
  FinanceSummary,
  Page,
  SubscriptionPlan,
  SubscriptionRow,
  SubscriptionStatus,
} from '@/api/types';
import { PageHeader } from '@/components/AppShell';
import { DataTable, PageSizeSelect, Pagination, type Column } from '@/components/DataTable';
import { usePageSize } from '@/lib/pageSize';
import { useToast } from '@/components/Toast';
import {
  Badge,
  Button,
  EmptyState,
  ErrorState,
  Field,
  Modal,
  Select,
  TableSkeleton,
  TextInput,
} from '@/components/ui';
import { count, date, money } from '@/lib/format';
import { useDebounced, useResource } from '@/lib/hooks';

const STATUS_TONE: Record<SubscriptionStatus, 'neutral' | 'positive' | 'caution' | 'critical' | 'info'> =
  {
    none: 'neutral',
    trialing: 'caution',
    active: 'positive',
    past_due: 'critical',
    canceled: 'neutral',
    vip: 'info',
  };

export function FinancePage() {
  const { t, i18n } = useTranslation();
  const toast = useToast();
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState<SubscriptionStatus | ''>('');
  const [page, setPage] = useState(1);
  const [editing, setEditing] = useState<SubscriptionRow | null>(null);
  const debouncedSearch = useDebounced(search);
  const language = i18n.language;
  const [pageSize, setPageSize] = usePageSize('finance');

  const summary = useResource<FinanceSummary>(
    '/api/v1/admin/finance/summary',
    undefined,
    [],
  );
  const rows = useResource<Page<SubscriptionRow>>(
    '/api/v1/admin/subscriptions',
    {
      search: debouncedSearch,
      status: status || undefined,
      page,
      page_size: pageSize,
    },
    [debouncedSearch, status, page, pageSize],
  );

  function reloadAll() {
    summary.reload();
    rows.reload();
  }

  const columns: Column<SubscriptionRow>[] = [
    {
      key: 'person',
      header: t('finance.person'),
      render: (row) => (
        <div className="min-w-0">
          <div className="truncate font-medium text-ink">
            {row.real_name || row.display_name}
          </div>
          <div className="truncate text-2xs text-ink-subtle">{row.email}</div>
        </div>
      ),
    },
    {
      key: 'club',
      header: t('people.club'),
      render: (row) => (
        <span className="text-ink-muted">
          {row.club_name ?? <span aria-hidden>—</span>}
        </span>
      ),
    },
    {
      key: 'status',
      header: t('finance.status'),
      render: (row) => (
        <Badge tone={STATUS_TONE[row.status]}>{t(`subscription.${row.status}`)}</Badge>
      ),
    },
    {
      key: 'plan',
      header: t('finance.plan'),
      render: (row) => (
        <div>
          <span className="text-ink-muted">
            {row.plan ? t(`subscription.${row.plan}`) : <span aria-hidden>—</span>}
          </span>
          {row.partner_rate ? (
            <div className="text-2xs text-ink-subtle">{t('finance.pricePartner')}</div>
          ) : null}
        </div>
      ),
    },
    {
      key: 'amount',
      header: t('finance.amount'),
      align: 'right',
      render: (row) =>
        row.amount_cents ? (
          <div>
            <div className="font-medium text-ink">
              {money(row.amount_cents, row.currency, language)}
            </div>
            <div className="text-2xs text-ink-subtle">
              {money(row.monthly_cents, row.currency, language)} {t('finance.perMonth')}
            </div>
          </div>
        ) : (
          <span aria-hidden className="text-ink-subtle">
            —
          </span>
        ),
    },
    {
      key: 'source',
      header: t('finance.source'),
      render: (row) => (
        <span className="text-ink-muted">
          {t(`subscription.sources.${row.source}`, { defaultValue: row.source })}
        </span>
      ),
    },
    {
      key: 'period',
      header: t('finance.renewsOn'),
      align: 'right',
      render: (row) => (
        <span className="text-ink-subtle">
          {date(row.current_period_end, language) ?? <span aria-hidden>—</span>}
        </span>
      ),
    },
    {
      key: 'edit',
      header: t('common.edit'),
      align: 'right',
      render: (row) => (
        <Button className="text-xs" onClick={() => setEditing(row)}>
          {t('common.edit')}
        </Button>
      ),
    },
  ];

  const data = summary.data;

  return (
    <>
      <PageHeader title={t('finance.title')} subtitle={t('finance.subtitle')} />

      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <Stat
          label={t('finance.mrr')}
          value={data ? money(data.mrr_cents, data.currency, language) : null}
          note={data ? t('overview.payingCount', { count: data.paying_count }) : undefined}
        />
        <Stat
          label={t('finance.arr')}
          value={data ? money(data.arr_cents, data.currency, language) : null}
        />
        <Stat
          label={t('finance.trialing')}
          value={data ? count(data.trialing_count, language) : null}
        />
        <Stat
          label={t('finance.vip')}
          value={data ? count(data.vip_count, language) : null}
          note={data ? t('finance.partnerRate') + ': ' + data.partner_rate_count : undefined}
        />
      </div>

      {/*
        The caveat sits directly under the figures, not in a footnote. A number
        labelled "revenue" that is actually a run-rate is the kind of thing
        somebody quotes in a board meeting.
      */}
      <div className="card mt-4 border-l-4 border-l-brand p-4">
        <h2 className="flex items-center gap-1.5 text-sm font-semibold text-ink">
          <Info aria-hidden className="h-4 w-4" />
          {t('finance.caveatTitle')}
        </h2>
        <p className="mt-1 text-sm text-ink-muted">{t('finance.caveatBody')}</p>

        {data ? (
          <dl className="mt-3 flex flex-wrap gap-x-6 gap-y-1 text-xs">
            <div className="flex gap-2">
              <dt className="text-ink-subtle">{t('finance.priceStandard')}</dt>
              <dd className="tabular text-ink">
                {money(data.price_table.standard.monthly, data.currency, language)}{' '}
                {t('finance.monthly')} ·{' '}
                {money(data.price_table.standard.yearly, data.currency, language)}{' '}
                {t('finance.yearly')}
              </dd>
            </div>
            <div className="flex gap-2">
              <dt className="text-ink-subtle">{t('finance.pricePartner')}</dt>
              <dd className="tabular text-ink">
                {money(data.price_table.partner.monthly, data.currency, language)}{' '}
                {t('finance.monthly')} ·{' '}
                {money(data.price_table.partner.yearly, data.currency, language)}{' '}
                {t('finance.yearly')}
              </dd>
            </div>
          </dl>
        ) : null}
      </div>

      <div className="card mt-4">
        <div className="flex flex-wrap items-center gap-3 border-b border-line p-3">
          <div className="relative min-w-[16rem] flex-1">
            <Search
              aria-hidden
              className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-subtle"
            />
            <TextInput
              type="search"
              aria-label={t('common.search')}
              placeholder={t('common.searchPlaceholder')}
              className="pl-9"
              value={search}
              onChange={(event) => {
                setSearch(event.target.value);
                setPage(1);
              }}
            />
          </div>
          <Select
            aria-label={t('finance.status')}
            value={status}
            onChange={(event) => {
              setStatus(event.target.value as SubscriptionStatus | '');
              setPage(1);
            }}
          >
            <option value="">{t('common.all')}</option>
            {(['active', 'trialing', 'vip', 'past_due', 'canceled'] as const).map(
              (value) => (
                <option key={value} value={value}>
                  {t(`subscription.${value}`)}
                </option>
              ),
            )}
          </Select>
          <PageSizeSelect
            value={pageSize}
            onChange={(next) => {
              setPageSize(next);
              setPage(1);
            }}
          />
        </div>

        {rows.error ? (
          <ErrorState message={t('common.loadFailed')} onRetry={rows.reload} />
        ) : rows.loading && !rows.data ? (
          <TableSkeleton columns={7} />
        ) : rows.data && rows.data.items.length === 0 ? (
          <EmptyState
            icon={<Wallet className="h-8 w-8" />}
            title={t('finance.emptyTitle')}
            body={t('finance.emptyBody')}
          />
        ) : (
          <>
            <DataTable
              caption={t('finance.title')}
              columns={columns}
              rows={rows.data?.items ?? []}
              rowKey={(row) => row.user_id}
            />
            <Pagination
              page={page}
              pageSize={pageSize}
              total={rows.data?.total ?? 0}
              onPage={setPage}
            />
          </>
        )}
      </div>

      {editing ? (
        <EditSubscription
          row={editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            toast.success(t('finance.saved'));
            reloadAll();
          }}
        />
      ) : null}
    </>
  );
}

function EditSubscription({
  row,
  onClose,
  onSaved,
}: {
  row: SubscriptionRow;
  onClose: () => void;
  onSaved: () => void;
}) {
  const { t } = useTranslation();
  const toast = useToast();
  const [status, setStatus] = useState<SubscriptionStatus>(row.status);
  const [plan, setPlan] = useState<SubscriptionPlan | ''>(row.plan ?? '');
  const [amount, setAmount] = useState('');
  const [note, setNote] = useState(row.note ?? '');
  const [busy, setBusy] = useState(false);

  // Neither is on a plan, so asking for one would be a field that does nothing.
  const needsPlan = status !== 'none' && status !== 'vip';

  async function save() {
    setBusy(true);
    try {
      await request(`/api/v1/admin/subscriptions/${row.user_id}`, {
        method: 'PUT',
        body: {
          status,
          plan: needsPlan ? plan || null : null,
          amount_cents: amount ? Math.round(Number(amount) * 100) : null,
          note: note || null,
        },
      });
      onSaved();
    } catch {
      toast.failure(t('common.loadFailed'));
      setBusy(false);
    }
  }

  return (
    <Modal
      open
      onClose={onClose}
      title={t('finance.editTitle', { name: row.real_name || row.display_name })}
      description={t('finance.editHint')}
      footer={
        <>
          <Button onClick={onClose}>{t('common.cancel')}</Button>
          <Button variant="primary" loading={busy} onClick={save}>
            {busy ? t('common.saving') : t('common.save')}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <Field label={t('finance.status')}>
          {(id) => (
            <Select
              id={id}
              className="w-full"
              value={status}
              onChange={(event) => setStatus(event.target.value as SubscriptionStatus)}
            >
              {(['none', 'trialing', 'active', 'past_due', 'canceled', 'vip'] as const).map(
                (value) => (
                  <option key={value} value={value}>
                    {t(`subscription.${value}`)}
                  </option>
                ),
              )}
            </Select>
          )}
        </Field>

        {needsPlan ? (
          <Field label={t('finance.plan')}>
            {(id) => (
              <Select
                id={id}
                className="w-full"
                value={plan}
                onChange={(event) => setPlan(event.target.value as SubscriptionPlan | '')}
              >
                <option value="">{t('common.none')}</option>
                <option value="monthly">{t('subscription.monthly')}</option>
                <option value="yearly">{t('subscription.yearly')}</option>
              </Select>
            )}
          </Field>
        ) : null}

        {needsPlan ? (
          <Field label={t('finance.customAmount')} hint={t('finance.customAmountHelp')}>
            {(id, describedBy) => (
              <TextInput
                id={id}
                aria-describedby={describedBy}
                type="number"
                inputMode="decimal"
                min={0}
                step="0.01"
                placeholder="4.90"
                value={amount}
                onChange={(event) => setAmount(event.target.value)}
              />
            )}
          </Field>
        ) : null}

        <Field label={t('finance.note')}>
          {(id) => (
            <TextInput
              id={id}
              value={note}
              maxLength={500}
              onChange={(event) => setNote(event.target.value)}
            />
          )}
        </Field>
      </div>
    </Modal>
  );
}

function Stat({
  label,
  value,
  note,
}: {
  label: string;
  value: string | null;
  note?: string;
}) {
  return (
    <div className="card p-4">
      <p className="text-xs text-ink-subtle">{label}</p>
      {value === null ? (
        <div className="mt-1 h-8 w-24 animate-pulse rounded bg-surface-sunken" />
      ) : (
        <p className="mt-1 text-2xl font-semibold tabular tracking-tight text-ink">
          {value}
        </p>
      )}
      {note ? <p className="mt-1 text-xs text-ink-subtle">{note}</p> : null}
    </div>
  );
}
